import { useState, useEffect } from 'react';
import { PayoffInputs, PayoffCalculationResults, PayoffMonthlyData, AfterTaxDetails, FilingStatus } from '../types';
import { STANDARD_DEDUCTIONS, SALT_CAPS } from '../constants';

export const calculateDeductibleInterest = (interest: number, mortgageBalance: number, filingStatus: FilingStatus) => {
    if (mortgageBalance <= 0) return 0;
    const limit = filingStatus === FilingStatus.MARRIED_FILING_SEPARATELY ? 375000 : 750000;
    const deductiblePortion = mortgageBalance > limit ? limit / mortgageBalance : 1;
    return interest * deductiblePortion;
};

const calculateMonthlyPayment = (principal: number, annualRate: number, months: number) => {
    if (annualRate === 0) return principal / months;
    const monthlyRate = annualRate / 100 / 12;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
};

export const calculatePayoffVsInvest = (inputs: PayoffInputs): PayoffCalculationResults | null => {
    const { 
        originalLoanAmount, 
        interestRate, 
        originationDate, 
        loanTerm, 
        homeValue, 
        appreciation, 
        extraPayment, 
        initialReturnRate, 
        initialReturnPeriod, 
        longTermReturnRate, 
        lumpSums, 
        marginalTaxRate, 
        taxRateOnGains, 
        filingStatus, 
        otherItemizedDeductions,
        isARM,
        armFixedTerm,
        armSubsequentRate
    } = inputs;

    if (!originalLoanAmount || !originationDate || !loanTerm) {
      return null;
    }

    // --- Time Setup ---
    const origination = new Date(originationDate);
    const now = new Date();
    // How many months have passed since origination?
    const monthsElapsedSinceOrigination = (now.getFullYear() - origination.getFullYear()) * 12 + (now.getMonth() - origination.getMonth());
    const totalOriginalMonths = loanTerm * 12;
    
    // Remaining term for simulation
    const remainingMonths = Math.max(0, totalOriginalMonths - monthsElapsedSinceOrigination);
    if (remainingMonths === 0) return null; // Loan is already over

    // --- Initial Balance Calculation (Historical) ---
    // We need to calculate the balance at "now" based on the historical amortization.
    // Logic: Iterate from month 1 to monthsElapsedSinceOrigination.
    // If ARM, check if rate changed during history.
    
    let currentActualBalance = originalLoanAmount;
    
    // Helper to get rate for a specific absolute month index (1 = first month of loan)
    const getRateForMonth = (monthIndex: number) => {
        if (isARM && monthIndex > armFixedTerm * 12) {
            return armSubsequentRate;
        }
        return interestRate;
    };

    let historicalPAndI = 0;
    // We need to simulate history to get accurate current balance, especially if rate changed in the past.
    // Usually, P&I is constant for fixed period, then changes.
    // We assume P&I was calculated at origination.
    let currentHistoricalPAndI = calculateMonthlyPayment(originalLoanAmount, interestRate, totalOriginalMonths);
    
    for(let m = 1; m <= monthsElapsedSinceOrigination; m++) {
        const rateForThisMonth = getRateForMonth(m);
        
        // If ARM adjustment happened exactly this month, P&I would have reset based on balance then.
        if (isARM && m === (armFixedTerm * 12) + 1) {
            const remaining = totalOriginalMonths - (m - 1);
            currentHistoricalPAndI = calculateMonthlyPayment(currentActualBalance, rateForThisMonth, remaining);
        }
        
        const monthlyRate = rateForThisMonth / 100 / 12;
        const interest = currentActualBalance * monthlyRate;
        const principal = currentHistoricalPAndI - interest;
        currentActualBalance -= principal;
    }
    
    // If simulation starts today, what is the *required* P&I?
    // It is whatever the current scheduled payment is.
    // If we are in fixed period, it's the original P&I.
    // If we are in adjustable, it was reset at the adjustment point.
    // Simplification: Recalculate P&I based on current balance and remaining term using CURRENT rate logic.
    // However, if we are mid-way through a fixed period, P&I is based on original loan amount.
    // Let's carry over `currentHistoricalPAndI` as the starting required payment.
    let currentRequiredPAndI = currentHistoricalPAndI;
    if (isNaN(currentRequiredPAndI) || currentRequiredPAndI <= 0) currentRequiredPAndI = 0; // Should not happen

    // --- Simulation Setup ---

    const monthlyAppreciationRate = appreciation / 100 / 12;
    const standardDeduction = STANDARD_DEDUCTIONS[filingStatus];
    const baseSaltCap = SALT_CAPS[filingStatus];
    const startYear = now.getFullYear();
    const sortedLumpSums = [...lumpSums].sort((a, b) => a.yearsFromNow - b.yearsFromNow);

    const monthlyData: PayoffMonthlyData[] = [];

    // --- Scenario 1: Payoff ---
    let payoffBalance = currentActualBalance;
    let payoffMonths = -1;
    let payoffInterest = 0;
    let payoffInvestBalance = 0;
    let totalPayoffContributions = 0;
    let payoffInterestThisYear = 0;
    let payoffDeductibleInterestThisYear = 0;
    
    // --- Scenario 2: Invest ---
    let originalBalance = currentActualBalance;
    let investBalance = 0;
    let totalInvestContributions = 0;
    let originalInterest = 0;
    let totalTaxSavingsInvested = 0;
    let investInterestThisYear = 0;
    let investDeductibleInterestThisYear = 0;
    // For Invest Scenario, we also need to track its required payment, which changes if ARM adjusts
    let investScenarioPAndI = currentRequiredPAndI; 
    let payoffScenarioPAndI = currentRequiredPAndI;

    let currentHomeValue = homeValue;
    let yearlyInvestTaxSavings: {[year: number]: number} = {};
    let yearlyPayoffTaxSavings: {[year: number]: number} = {};

    let lumpSumIndexPayoff = 0;
    let lumpSumIndexInvest = 0;
    const itemizedYears: number[] = [];

    // --- Apply Year 0 Lump Sums ---
    while (lumpSumIndexPayoff < sortedLumpSums.length && sortedLumpSums[lumpSumIndexPayoff].yearsFromNow <= 0) {
        payoffBalance -= sortedLumpSums[lumpSumIndexPayoff].amount;
        lumpSumIndexPayoff++;
    }
    
    if (payoffBalance <= 0) {
        payoffMonths = 0;
    }

    while (lumpSumIndexInvest < sortedLumpSums.length && sortedLumpSums[lumpSumIndexInvest].yearsFromNow <= 0) {
        investBalance += sortedLumpSums[lumpSumIndexInvest].amount;
        totalInvestContributions += sortedLumpSums[lumpSumIndexInvest].amount;
        lumpSumIndexInvest++;
    }

    for (let month = 1; month <= remainingMonths; month++) {
      const currentDate = new Date(now);
      currentDate.setMonth(now.getMonth() + month - 1);
      const currentYear = currentDate.getFullYear();
      
      const yearsIntoSim = (month - 1) / 12;
      const monthlyReturnRate = yearsIntoSim < initialReturnPeriod ? initialReturnRate / 100 / 12 : longTermReturnRate / 100 / 12;

      // Determine Interest Rate for this month
      const absoluteMonthIndex = monthsElapsedSinceOrigination + month;
      const currentAnnualRate = getRateForMonth(absoluteMonthIndex);
      const monthlyInterestRate = currentAnnualRate / 100 / 12;

      // Check for ARM Adjustment Trigger to recalibrate P&I
      if (isARM && absoluteMonthIndex === (armFixedTerm * 12) + 1) {
          // Rate just changed. Recast payments.
          const remaining = totalOriginalMonths - (absoluteMonthIndex - 1);
          
          // Invest Scenario: Recast based on its scheduled balance
          if (originalBalance > 0) {
            investScenarioPAndI = calculateMonthlyPayment(originalBalance, currentAnnualRate, remaining);
          }
          
          // Payoff Scenario: Recast based on ITS scheduled balance? 
          if (payoffBalance > 0) {
              payoffScenarioPAndI = calculateMonthlyPayment(payoffBalance, currentAnnualRate, remaining);
          }
      }


      // --- Payoff calculation ---
      while (lumpSumIndexPayoff < sortedLumpSums.length && Math.round(sortedLumpSums[lumpSumIndexPayoff].yearsFromNow * 12) === month) {
          payoffBalance -= sortedLumpSums[lumpSumIndexPayoff].amount;
          lumpSumIndexPayoff++;
      }
      
      if (payoffBalance <= 0 && payoffMonths === -1) {
          payoffMonths = month;
      }
      
      if (payoffBalance > 0) {
        const interest = payoffBalance * monthlyInterestRate;
        payoffInterest += interest;
        payoffInterestThisYear += interest;
        payoffDeductibleInterestThisYear += calculateDeductibleInterest(interest, payoffBalance, filingStatus);
        
        // Principal = Payment - Interest. Payment = Required + Extra.
        let principal = (payoffScenarioPAndI + extraPayment) - interest;
        
        // Cap principal to balance
        if (principal > payoffBalance) principal = payoffBalance;
        
        payoffBalance -= principal;
        
        if (payoffBalance <= 0 && payoffMonths === -1) {
            payoffMonths = month;
        }
      } else { 
        const monthlyInvestment = payoffScenarioPAndI + extraPayment;
        payoffInvestBalance = (payoffInvestBalance * (1 + monthlyReturnRate)) + monthlyInvestment;
        totalPayoffContributions += monthlyInvestment;
      }
      
      // --- Invest calculation ---
      while (lumpSumIndexInvest < sortedLumpSums.length && Math.round(sortedLumpSums[lumpSumIndexInvest].yearsFromNow * 12) === month) {
          const lump = sortedLumpSums[lumpSumIndexInvest];
          investBalance += lump.amount;
          totalInvestContributions += lump.amount;
          lumpSumIndexInvest++;
      }
      
      const originalInterestThisMonth = originalBalance > 0 ? originalBalance * monthlyInterestRate : 0;
      originalInterest += originalInterestThisMonth;
      investInterestThisYear += originalInterestThisMonth;
      investDeductibleInterestThisYear += calculateDeductibleInterest(originalInterestThisMonth, originalBalance, filingStatus);
      
      let originalPrincipal = 0;
      if (originalBalance > 0) {
          originalPrincipal = investScenarioPAndI - originalInterestThisMonth;
          if (originalPrincipal > originalBalance) originalPrincipal = originalBalance;
          originalBalance -= originalPrincipal;
      }

      investBalance = (investBalance * (1 + monthlyReturnRate)) + extraPayment;
      totalInvestContributions += extraPayment;

      currentHomeValue *= (1 + monthlyAppreciationRate);

      // --- Annual Tax Calculation ---
      if (currentDate.getMonth() === 11 || month === remainingMonths) {
        const yearsSinceStart = currentYear - startYear;
        const currentYearSaltCap = baseSaltCap * Math.pow(1.01, yearsSinceStart);
        const cappedOtherDeductions = Math.min(otherItemizedDeductions, currentYearSaltCap);

        // Invest Scenario
        const totalDeductionsInvest = investDeductibleInterestThisYear + cappedOtherDeductions;
        if (totalDeductionsInvest > standardDeduction) {
          if (!itemizedYears.includes(currentYear)) itemizedYears.push(currentYear);
          const deductionWithoutMortgage = Math.max(standardDeduction, cappedOtherDeductions);
          const benefitFromMortgage = totalDeductionsInvest - deductionWithoutMortgage;
          const taxSavings = benefitFromMortgage * (marginalTaxRate / 100);

          investBalance += taxSavings; // Reinvest at year-end
          totalInvestContributions += taxSavings; // It is a contribution
          totalTaxSavingsInvested += taxSavings;
          yearlyInvestTaxSavings[currentYear] = (yearlyInvestTaxSavings[currentYear] || 0) + taxSavings;
        }
        investInterestThisYear = 0; // Reset for next year
        investDeductibleInterestThisYear = 0;

        // Payoff Scenario
        const totalDeductionsPayoff = payoffDeductibleInterestThisYear + cappedOtherDeductions;
        if (totalDeductionsPayoff > standardDeduction) {
          const deductionWithoutMortgage = Math.max(standardDeduction, cappedOtherDeductions);
          const benefitFromMortgage = totalDeductionsPayoff - deductionWithoutMortgage;
          const taxSavings = benefitFromMortgage * (marginalTaxRate / 100);
          payoffInvestBalance += taxSavings;
          totalPayoffContributions += taxSavings;
          yearlyPayoffTaxSavings[currentYear] = (yearlyPayoffTaxSavings[currentYear] || 0) + taxSavings;
        }
        payoffInterestThisYear = 0; // Reset for next year
        payoffDeductibleInterestThisYear = 0;
      }
      
      monthlyData.push({
        month,
        year: currentYear,
        // Payoff
        payoffBalance: Math.max(0, payoffBalance),
        payoffInvestBalance,
        payoffNetWorth: currentHomeValue - Math.max(0, payoffBalance) + payoffInvestBalance,
        payoffTaxSavingsThisYear: yearlyPayoffTaxSavings[currentYear] || 0,
        payoffHomeEquity: currentHomeValue - Math.max(0, payoffBalance),
        // Invest
        originalBalance: Math.max(0, originalBalance),
        investBalance,
        investNetWorth: currentHomeValue - Math.max(0, originalBalance) + investBalance,
        taxSavingsThisYear: yearlyInvestTaxSavings[currentYear] || 0,
        investHomeEquity: currentHomeValue - Math.max(0, originalBalance),
      });
    }

    if (payoffMonths === -1) payoffMonths = remainingMonths;
    const finalMonthData = monthlyData[monthlyData.length - 1];

    // After-tax calculations
    const calculateAfterTax = (finalValue: number, totalContributions: number): AfterTaxDetails => {
        const finalGains = Math.max(0, finalValue - totalContributions);
        const taxes = finalGains * (taxRateOnGains / 100);
        return {
            finalValue,
            finalGains,
            taxes,
            finalValueAfterTax: finalValue - taxes,
        };
    };
    
    const payoffInvestmentDetails = calculateAfterTax(finalMonthData.payoffInvestBalance, totalPayoffContributions);
    const investInvestmentDetails = calculateAfterTax(finalMonthData.investBalance, totalInvestContributions);

    // FIX: Using Pre-Tax Investment Value for "Net Worth" Summary to match the Table display
    const finalPayoffNetWorth = finalMonthData.payoffHomeEquity + payoffInvestmentDetails.finalValue;
    const finalInvestNetWorth = finalMonthData.investHomeEquity + investInvestmentDetails.finalValue;

    const newPayoffDate = new Date(now);
    newPayoffDate.setMonth(newPayoffDate.getMonth() + payoffMonths);
    const yearsShortened = (remainingMonths - payoffMonths) / 12;

    return {
      summary: {
        finalPayoffNetWorth,
        finalInvestNetWorth,
        payoffInvestmentDetails,
        investInvestmentDetails,
        interestSaved: originalInterest - payoffInterest,
        newPayoffDate: newPayoffDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long'}),
        yearsShortened: parseFloat(yearsShortened.toFixed(1)),
        totalTaxSavingsInvested,
        itemizationInfo: {
          standardDeduction,
          itemizedYears: itemizedYears.sort((a,b) => a - b),
        }
      },
      monthlyData,
    };
};

export const usePayoffVsInvestCalculator = (inputs: PayoffInputs | null): PayoffCalculationResults | null => {
  const [results, setResults] = useState<PayoffCalculationResults | null>(null);

  useEffect(() => {
    if (!inputs) {
      setResults(null);
      return;
    }

    const calculationResults = calculatePayoffVsInvest(inputs);
    setResults(calculationResults);

  }, [inputs]);

  return results;
};